import { LicenseType } from './LicenseType';
import { Feature } from '@nirvana/shared/constants';
import { MonthlyProductType } from './MonthlyProductType';
import { AvailableBundles } from './Bundles';
import { ProductServiceStatus } from './ProductServiceStatus';

export interface CompanyLicenseInformation {
  reservationSupported?: boolean;
  minSlotsToReserve?: number;
  maxSlotsToReserve?: number;

  hasMonthlyLicense: boolean;
  parentHasMspTrialLicense: boolean;

  parentCompanyFeatures?: { [featureId in Feature]: boolean };
  editedCompanyFeatures?: { [featureId in Feature]: boolean };

  toggleableFeatures?: Feature[];
  availableBundles?: AvailableBundles;

  editedCompany: {
    licenseType: LicenseType | null;
    inheritedLicense: boolean;
    hasMspTrial: boolean;
  };

  parentCompany: {
    mdrManagerStatus: ProductServiceStatus | null;
    mdrServiceStatus: ProductServiceStatus | null;
  };

  // default: null; if company has FRAT subscription license: true; if company has FRAT end-user license: false
  // note: (hasFratSubscription === null) means the company has a non-FRAT license
  hasFratSubscription: boolean | null;

  // default: null; if parent company has FRAT subscription license: true
  parentHasFratSubscription: boolean | null;

  parentEndSubscriptionEndDateLimit: string | null;

  hasBasicMonthlySubscription: boolean;
  parentHasBasicMonthlySubscription: boolean | null;
  parentIsBasicSecurityProvider: boolean | null;

  parentIsFratManager: boolean | null;

  licenseType: LicenseType | null;
}

export namespace CompanyLicenseInformation {
  export function hasFratEndUser(companyLicenseInformation: CompanyLicenseInformation): boolean {
    // note: (hasFratSubscription === null) means the company has a non-FRAT license
    return companyLicenseInformation.hasFratSubscription === false;
  }

  export function doesParentCompanyHaveFratSubscription(companyLicenseInformation: CompanyLicenseInformation): boolean {
    return companyLicenseInformation.parentHasFratSubscription === true;
  }

  export function getParentDelegableProducts(licenseInfo: CompanyLicenseInformation): MonthlyProductType[] {
    return MonthlyProductType.getAllProductTypes().filter((productType: MonthlyProductType) => {
      // get the delegate featureId for this product type
      const associatedDelegateFeatureId = MonthlyProductType.getDelegateFeatureId(productType);
      // check if the parent company has it
      return CompanyLicenseInformation.doesParentCompanyHaveFeature(licenseInfo, associatedDelegateFeatureId);
    });
  }

  export function doesParentCompanyHaveFeature(licenseInfo: CompanyLicenseInformation, feature: Feature): boolean {
    if (!licenseInfo.parentCompanyFeatures) {
      return false;
    }

    return Feature.isFeatureAvailableForCompany(licenseInfo.parentCompanyFeatures, feature);
  }

  export function getEditedCompanyAssignedProductType(licenseInfo: CompanyLicenseInformation): MonthlyProductType {
    // the default product type does not have a feature id, so we're checking for _all other_ product types first
    // if no product feature flag is set, then we __assume__ the assigned product is the default one
    if (!licenseInfo.editedCompanyFeatures) {
      // default product type
      return MonthlyProductType.EndpointSecurity;
    }

    if (licenseInfo.editedCompanyFeatures[Feature.PRODUCT_FEATURE_COMPANION_USABLE]) {
      return MonthlyProductType.BitdefenderEdr;
    }

    // default product type
    return MonthlyProductType.EndpointSecurity;
  }
}
